import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export function DrawManager({ api, classId, className }) {
    const queryClient = useQueryClient();
    const [showDraw, setShowDraw] = useState(false);

    // Fetch current draw
    const { data: drawData, isLoading: isLoadingDraw } = useQuery({
        queryKey: ['draw', classId],
        queryFn: () => api.getClassDraw(classId),
        enabled: !!classId,
    });

    // Generate draw mutation
    const generateDrawMutation = useMutation({
        mutationFn: () => api.generateClassDraw(classId),
        onSuccess: () => {
            queryClient.invalidateQueries(['draw', classId]);
            queryClient.invalidateQueries(['participants', classId]);
        },
    });

    // Clear draw mutation
    const clearDrawMutation = useMutation({
        mutationFn: () => api.clearClassDraw(classId),
        onSuccess: () => {
            queryClient.invalidateQueries(['draw', classId]);
            queryClient.invalidateQueries(['participants', classId]);
        },
    });

    // Scratch participant mutation
    const scratchMutation = useMutation({
        mutationFn: ({ participantId }) => api.scratchParticipant(classId, participantId),
        onSuccess: () => {
            queryClient.invalidateQueries(['draw', classId]);
            queryClient.invalidateQueries(['participants', classId]);
        },
    });

    const handleGenerateDraw = () => {
        if (window.confirm('Generate a new random draw for this class? This will assign random draw numbers to all participants.')) {
            generateDrawMutation.mutate();
        }
    };

    const handleClearDraw = () => {
        if (window.confirm('Clear the current draw? This will remove all draw numbers and return to signup order.')) {
            clearDrawMutation.mutate();
        }
    };

    const handleScratch = (participantId, participantName) => {
        if (window.confirm(`Mark ${participantName} as scratched? They will be removed from the running order but their draw number will be preserved.`)) {
            scratchMutation.mutate({ participantId });
        }
    };

    const hasDraw = drawData?.draw && drawData.draw.length > 0;
    const activeParticipants = hasDraw ? drawData.draw.filter(p => !p.isScratched) : [];
    const scratchedParticipants = hasDraw ? drawData.draw.filter(p => p.isScratched) : [];

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-foreground">Draw Management</h3>
                <div className="flex gap-2">
                    {!hasDraw ? (
                        <button
                            onClick={handleGenerateDraw}
                            disabled={generateDrawMutation.isPending}
                            className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-3"
                        >
                            {generateDrawMutation.isPending ? 'Generating...' : 'Generate Draw'}
                        </button>
                    ) : (
                        <>
                            <button
                                onClick={() => setShowDraw(!showDraw)}
                                className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-3"
                            >
                                {showDraw ? 'Hide Draw' : 'View Draw'}
                            </button>
                            <button
                                onClick={handleClearDraw}
                                disabled={clearDrawMutation.isPending}
                                className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-destructive text-destructive-foreground hover:bg-destructive/90 h-9 px-3"
                            >
                                {clearDrawMutation.isPending ? 'Clearing...' : 'Clear Draw'}
                            </button>
                        </>
                    )}
                </div>
            </div>

            {hasDraw && (
                <div className="text-sm text-muted-foreground">
                    <p>Draw generated on: {new Date(drawData.generatedAt).toLocaleString()}</p>
                    <p>Total participants: {drawData.participantCount}</p>
                    <p>Active participants: {activeParticipants.length}</p>
                    {scratchedParticipants.length > 0 && (
                        <p>Scratched participants: {scratchedParticipants.length}</p>
                    )}
                </div>
            )}

            {showDraw && hasDraw && (
                <div className="space-y-4">
                    {/* Active Participants */}
                    <div>
                        <h4 className="font-medium text-foreground mb-2">Running Order</h4>
                        <div className="space-y-2">
                            {activeParticipants.map((participant) => (
                                <div
                                    key={participant.participantId}
                                    className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="flex items-center justify-center w-8 h-8 bg-primary text-primary-foreground rounded-full text-sm font-semibold">
                                            {participant.drawNumber}
                                        </div>
                                        <div>
                                            <div className="font-medium text-foreground">
                                                {participant.participantName}
                                            </div>
                                            <div className="text-sm text-muted-foreground">
                                                {participant.homeTown} • {participant.horses?.join(', ') || 'No horses'}
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleScratch(participant.participantId, participant.participantName)}
                                        disabled={scratchMutation.isPending}
                                        className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-8 px-2"
                                    >
                                        Scratch
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Scratched Participants */}
                    {scratchedParticipants.length > 0 && (
                        <div>
                            <h4 className="font-medium text-foreground mb-2">Scratched Participants</h4>
                            <div className="space-y-2">
                                {scratchedParticipants.map((participant) => (
                                    <div
                                        key={participant.participantId}
                                        className="flex items-center gap-4 p-3 bg-red-50 rounded-lg border border-red-200"
                                    >
                                        <div className="flex items-center justify-center w-8 h-8 bg-red-500 text-white rounded-full text-sm font-semibold line-through">
                                            {participant.drawNumber}
                                        </div>
                                        <div>
                                            <div className="font-medium text-foreground line-through">
                                                {participant.participantName}
                                            </div>
                                            <div className="text-sm text-muted-foreground">
                                                {participant.homeTown} • {participant.horses?.join(', ') || 'No horses'}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {!hasDraw && !isLoadingDraw && (
                <div className="text-center p-6 bg-muted/30 rounded-lg border">
                    <p className="text-muted-foreground mb-2">No draw has been generated for this class.</p>
                    <p className="text-sm text-muted-foreground">
                        Participants will run in the order they signed up until a draw is generated.
                    </p>
                </div>
            )}

            {isLoadingDraw && (
                <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    <span className="ml-2">Loading draw...</span>
                </div>
            )}
        </div>
    );
} 